import { Fragment, useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
    MagnifyingGlassIcon,
    XMarkIcon,
    UserCircleIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import useGlobalSearch, { type SearchUser } from "@/hooks/useGlobalSearch";



function highlight(text: string, query: string): React.ReactNode {
    if (!text || !query.trim()) return text;
    const idx = text.toLowerCase().indexOf(query.trim().toLowerCase());
    if (idx === -1) return text;
    return (
        <>
            {text.slice(0, idx)}
            <mark className="bg-yellow-200 text-gray-900 rounded-sm px-0.5 not-italic">
                {text.slice(idx, idx + query.length)}
            </mark>
            {text.slice(idx + query.length)}
        </>
    );
}


interface SearchResultItemProps {
    user: SearchUser;
    query: string;
    isActive: boolean;
    onSelect: (user: SearchUser) => void;
    onMouseEnter: () => void;
}

function SearchResultItem({ user, query, isActive, onSelect, onMouseEnter }: SearchResultItemProps) {
    return (
        <li
            role="option"
            aria-selected={isActive}
            onMouseEnter={onMouseEnter}
            onClick={() => onSelect(user)}
            className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                isActive ? "bg-primary-50" : "hover:bg-gray-50"
            }`}
        >
            <UserCircleIcon className="h-9 w-9 flex-shrink-0 text-gray-300" />
            <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">
                    {highlight(user.name, query)}
                </p>
                <p className="text-xs text-gray-500 truncate">
                    {highlight(user.email, query)}
                </p>
                {(user.mobile || user.phone) && (
                    <p className="text-xs text-gray-400 truncate">
                        {highlight((user.mobile ?? user.phone) as string, query)}
                    </p>
                )}
            </div>
            <span
                className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${
                    user.status === "Active"
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-100 text-gray-500"
                }`}
            >
                {user.status}
            </span>
        </li>
    );
}

interface GlobalSearchProps {
    isOpen: boolean;
    onClose: () => void;
}

function GlobalSearch({ isOpen, onClose }: GlobalSearchProps) {
    const navigate = useNavigate();
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const [activeIndex, setActiveIndex] = useState(-1);

    const { query, results, isLoading, hasSearched, handleQueryChange, reset } = useGlobalSearch();

    const handleClose = () => {
        reset();
        setActiveIndex(-1);
        onClose();
    };

    const handleSelect = (user: SearchUser) => {
        handleClose();
        navigate(`/users/update/${user.id}`);
    };

    // Reset active index whenever results change
    useEffect(() => {
        setActiveIndex(-1);
    }, [results]);

    // Focus the input when the modal opens
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [isOpen]);

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (results.length === 0) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
        } else if (e.key === "Enter" && activeIndex >= 0) {
            e.preventDefault();
            handleSelect(results[activeIndex]);
        }
    };

    // Scroll active item into view
    useEffect(() => {
        if (activeIndex >= 0 && listRef.current) {
            const item = listRef.current.children[activeIndex] as HTMLElement;
            item?.scrollIntoView({ block: "nearest" });
        }
    }, [activeIndex]);

    const showEmpty = hasSearched && !isLoading && results.length === 0;
    const showResults = results.length > 0;

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-[400]" onClose={handleClose}>
                {/* Backdrop */}
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm" />
                </Transition.Child>

                {/* Modal panel */}
                <div className="fixed inset-0 flex items-start justify-center pt-[15vh] px-4">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0 scale-95 -translate-y-2"
                        enterTo="opacity-100 scale-100 translate-y-0"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100 scale-100 translate-y-0"
                        leaveTo="opacity-0 scale-95 -translate-y-2"
                    >
                        <Dialog.Panel
                            className="w-full max-w-xl bg-white rounded-2xl shadow-2xl ring-1 ring-black/5 overflow-hidden"
                            role="combobox"
                            aria-expanded={showResults}
                            aria-haspopup="listbox"
                        >
                            {/* Search input row */}
                            <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
                                {isLoading ? (
                                    <svg
                                        className="h-5 w-5 text-primary-500 animate-spin flex-shrink-0"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                    </svg>
                                ) : (
                                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                                )}
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={query}
                                    onChange={(e) => handleQueryChange(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Search by name, email or number…"
                                    className="flex-1 bg-transparent text-gray-900 placeholder-gray-400 text-base outline-none"
                                    aria-autocomplete="list"
                                    aria-controls="global-search-results"
                                    aria-activedescendant={
                                        activeIndex >= 0 ? `search-result-${results[activeIndex]?.id}` : undefined
                                    }
                                    autoComplete="off"
                                    spellCheck={false}
                                />
                                {query && (
                                    <button
                                        type="button"
                                        onClick={() => handleQueryChange("")}
                                        className="flex-shrink-0 rounded-md p-0.5 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        aria-label="Clear search"
                                    >
                                        <XMarkIcon className="h-4 w-4" />
                                    </button>
                                )}
                            </div>

                            {/* Results */}
                            {showResults && (
                                <ul
                                    id="global-search-results"
                                    ref={listRef}
                                    role="listbox"
                                    aria-label="Search results"
                                    className="max-h-80 overflow-y-auto divide-y divide-gray-50"
                                >
                                    {results.map((user, idx) => (
                                        <SearchResultItem
                                            key={user.id}
                                            user={user}
                                            query={query}
                                            isActive={idx === activeIndex}
                                            onSelect={handleSelect}
                                            onMouseEnter={() => setActiveIndex(idx)}
                                        />
                                    ))}
                                </ul>
                            )}

                            {/* Empty state */}
                            {showEmpty && (
                                <div className="px-4 py-10 text-center">
                                    <p className="text-sm font-medium text-gray-500">
                                        No results for <span className="text-gray-900">"{query}"</span>
                                    </p>
                                    <p className="mt-1 text-xs text-gray-400">
                                        Try searching by full name, email address or phone number.
                                    </p>
                                </div>
                            )}

                            {/* Idle hint */}
                            {!hasSearched && !isLoading && (
                                <div className="px-4 py-6 text-center">
                                    <p className="text-xs text-gray-400">
                                        Type at least 2 characters to search users
                                    </p>
                                </div>
                            )}

                            {/* Footer with keyboard hints */}
                            <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-400">
                                <div className="flex items-center gap-3">
                                    <span className="flex items-center gap-1">
                                        <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-[10px] font-medium shadow-sm">↑↓</kbd>
                                        Navigate
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-[10px] font-medium shadow-sm">↵</kbd>
                                        Select
                                    </span>
                                </div>
                                <span className="flex items-center gap-1">
                                    <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-[10px] font-medium shadow-sm">Esc</kbd>
                                    Close
                                </span>
                            </div>
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
}

export default GlobalSearch;
